import React from "react";
import { BarChart, CartesianGrid, XAxis, YAxis, Legend, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { Select } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Body, Heading } from "../../../components/Typography";
import { ChartTooltip } from "../../../components/graphs/ChartTooltip";
import { ChartLegend } from "../../../components/graphs/ChartLegend";
import { useStyles } from "./styles";
import { useYtdData } from "./useYtdData";
import { GraphQL } from "@rpi/openapi-api";
import { TransformedOrderDataPoint, fillMissingOrderMonths, formatYearMonth } from "./transformer";
import { useAuthUser } from "../../../hooks/useAuthUser";

type TransformedOrdersChartQuery = {
  ordersChart: {
    availableYears: number[];
    dataPoints: TransformedOrderDataPoint[];
  };
};

export const YtdOrdersChart: React.FC = () => {
  const user = useAuthUser();
  const { classes } = useStyles();
  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());

  const { data, isLoading } = GraphQL.useOrdersChartQuery(
    {
      isSandbox: user.isSandbox,
      timezone,
      year: selectedYear,
    },
    {
      select: (data: GraphQL.OrdersChartQuery): TransformedOrdersChartQuery => ({
        ordersChart: {
          availableYears: data.ordersChart?.availableYears ?? [],
          dataPoints: fillMissingOrderMonths(data.ordersChart?.dataPoints ?? [], selectedYear),
        },
      }),
    }
  );

  const { selectProps } = useYtdData({
    isLoading,
    availableYears: data?.ordersChart?.availableYears,
    selectedYear,
    setSelectedYear,
  });

  const chartData = data?.ordersChart?.dataPoints ?? [];

  return (
    <div className={classes.chartContainer}>
      <div className={classes.chartHeader}>
        <Heading size="lg">Orders</Heading>
        <Select className={classes.chartSelect} {...selectProps} />
      </div>
      <Body className={classes.chartSubheader}>Year-to-date</Body>
      <ResponsiveContainer className={classes.chart}>
        <BarChart data={chartData}>
          <CartesianGrid />
          <XAxis
            dataKey="month"
            axisLine={false}
            tickFormatter={(value: string) => formatYearMonth(value, { month: "short" })}
          />
          <YAxis
            dataKey="shipped"
            width="auto"
            domain={[0, "auto"]}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => value} // This fixes a bug where the tick values just don't show up
          />
          <Tooltip
            content={(props) => {
              const monthDate = props?.payload?.[0]?.payload.month as string | undefined;
              const label = monthDate ? formatYearMonth(monthDate, { month: "short", year: "numeric" }) : "";
              return <ChartTooltip {...props} label={label} />;
            }}
            cursor={{
              fill: tokens.color.semantic.bg.neutralInverseHover,
              fillOpacity: 0.06,
            }}
          />
          <Legend content={ChartLegend} />
          <Bar
            dataKey="shipped"
            name="Shipped"
            stackId="a"
            fill={tokens.color.primitive.light.dataVisualization.green}
          />
          <Bar
            dataKey="printing"
            name="Printing"
            stackId="a"
            fill={tokens.color.primitive.light.dataVisualization.blue}
          />
          <Bar
            dataKey="inProgress"
            name="In Progress"
            stackId="a"
            fill={tokens.color.primitive.light.dataVisualization.orange}
          />
          <Bar
            dataKey="failed"
            name="Failed"
            stackId="a"
            fill={tokens.color.primitive.light.dataVisualization.magenta}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
